import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';

// ── Design tokens ─────────────────────────────────────────────────────────────

const C = {
    PURPLE: '#6344d4',
    PURPLE_STRONG: '#6d4ed5',
    PURPLE_SOFT: '#ede9fe',
    NAVY: '#1e3a8a',
    INK: '#0f172a',
    INK_700: '#334155',
    SLATE: '#64748b',
    SLATE_LIGHT: '#94a3b8',
    HAIR: '#e2e8f0',
    HAIR_SOFT: '#f1f5f9',
    RED: '#dc2626',
    RED_BG: '#fef2f2',
};

const LIST_COLORS = [
    '#1e3a8a',
    '#7c3aed',
    '#16a34a',
    '#dc2626',
    '#d97706',
    '#0891b2',
];

// ── Icon paths ────────────────────────────────────────────────────────────────

const ICON_PATHS: Record<string, string> = {
    trending_up:  'M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z',
    bell:         'M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z',
    sparkle:      'M19 9l1.25-2.75L23 5l-2.75-1.25L19 1l-1.25 2.75L15 5l2.75 1.25L19 9zm-7.5.5L9 4 6.5 9.5 1 12l5.5 2.5L9 20l2.5-5.5L17 12l-5.5-2.5zM19 15l-1.25 2.75L15 19l2.75 1.25L19 23l1.25-2.75L23 19l-2.75-1.25L19 15z',
    archive:      'M20.54 5.23l-1.39-1.68A1.45 1.45 0 0018 3H6c-.47 0-.88.21-1.16.55L3.46 5.23C3.17 5.57 3 6.02 3 6.5V19a2 2 0 002 2h14c1.1 0 2-.9 2-2V6.5c0-.48-.17-.93-.46-1.27zM12 17.5L6.5 12H10v-2h4v2h3.5L12 17.5zM5.12 5l.81-1h12l.94 1H5.12z',
    exit:         'M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z',
};

// ── Icon component ────────────────────────────────────────────────────────────

interface IconProps {
    name: string;
    size?: number;
    color?: string;
}

function Icon({ name, size = 18, color = C.INK }: IconProps) {
    const path = ICON_PATHS[name];
    if (!path) {
        return <span style={{ width: size, height: size, display: 'inline-block', flexShrink: 0 }} />;
    }
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill={color}
            style={{ display: 'inline-block', flexShrink: 0 }}
        >
            <path d={path} />
        </svg>
    );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const S = {
    sidebar: {
        width: 240,
        flexShrink: 0,
        background: '#fff',
        borderRight: `1px solid ${C.HAIR}`,
        display: 'flex',
        flexDirection: 'column',
        padding: '20px 14px',
        height: '100%',
        boxSizing: 'border-box',
    } as React.CSSProperties,

    brand: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '4px 10px 16px',
        borderBottom: `1px solid ${C.HAIR_SOFT}`,
    } as React.CSSProperties,

    brandMark: {
        width: 32,
        height: 32,
        borderRadius: 9,
        background: `linear-gradient(135deg, ${C.PURPLE_STRONG}, ${C.NAVY})`,
        color: '#fff',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 800,
        fontSize: 14,
        flexShrink: 0,
    } as React.CSSProperties,

    brandText: {
        fontSize: 16,
        fontWeight: 800,
        letterSpacing: '-0.01em',
    } as React.CSSProperties,

    section: {
        fontSize: 10,
        fontWeight: 700,
        color: C.SLATE_LIGHT,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        padding: '18px 10px 8px',
    } as React.CSSProperties,

    navItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        borderRadius: 8,
        fontSize: 13,
        color: C.INK_700,
        fontWeight: 600,
        cursor: 'pointer',
        textDecoration: 'none',
    } as React.CSSProperties,

    navItemActive: {
        background: C.PURPLE_SOFT,
        color: C.PURPLE_STRONG,
    } as React.CSSProperties,

    budgetList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        marginTop: 4,
    } as React.CSSProperties,

    budgetItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 12px',
        borderRadius: 8,
        fontSize: 13,
        color: C.INK_700,
        fontWeight: 500,
        cursor: 'pointer',
    } as React.CSSProperties,

    budgetDot: {
        width: 8,
        height: 8,
        borderRadius: '50%',
        flexShrink: 0,
    } as React.CSSProperties,

    userSection: {
        marginTop: 'auto',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        borderTop: `1px solid ${C.HAIR_SOFT}`,
    } as React.CSSProperties,

    userAvatar: {
        width: 32,
        height: 32,
        borderRadius: '50%',
        background: C.PURPLE,
        color: '#fff',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 13,
        fontWeight: 700,
        flexShrink: 0,
    } as React.CSSProperties,

    userInfo: {
        flex: 1,
        minWidth: 0,
    } as React.CSSProperties,

    userName: {
        fontSize: 13,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    } as React.CSSProperties,

    userEmail: {
        fontSize: 11,
        color: C.SLATE,
        marginTop: 1,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    } as React.CSSProperties,

    logoutBtn: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: 4,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 6,
        flexShrink: 0,
    } as React.CSSProperties,
};

// ── Props ─────────────────────────────────────────────────────────────────────

export interface SidebarBudget {
    id: string;
    displayName: string;
}

interface SidebarProps {
    budgets: SidebarBudget[];
    userId: string;
    userEmail: string;
    onSelectBudget: (budget: SidebarBudget) => void;
    onLogout: () => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function Sidebar({ budgets, userId: _userId, userEmail, onSelectBudget, onLogout }: SidebarProps) {
    const history = useHistory();
    const location = useLocation();

    const emailStr = String(userEmail || '');
    const avatarInitial = emailStr.charAt(0).toUpperCase() || 'U';

    const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');

    const homeStyle = isActive('/home')
        ? { ...S.navItem, ...S.navItemActive }
        : S.navItem;

    const suggestionsStyle = isActive('/suggestions')
        ? { ...S.navItem, ...S.navItemActive }
        : S.navItem;

    const templatesStyle = isActive('/templates')
        ? { ...S.navItem, ...S.navItemActive }
        : S.navItem;

    const homeIconColor = isActive('/home') ? C.PURPLE_STRONG : C.SLATE;
    const suggestionsIconColor = isActive('/suggestions') ? C.PURPLE_STRONG : C.SLATE;
    const templatesIconColor = isActive('/templates') ? C.PURPLE_STRONG : C.SLATE;

    const visibleBudgets = budgets.slice(0, 5);

    return (
        <div style={S.sidebar} data-testid="sidebar">
            {/* Brand */}
            <div style={S.brand}>
                <div style={S.brandMark}>U</div>
                <div style={S.brandText}>Uplift</div>
            </div>

            {/* Workspace nav */}
            <div style={S.section}>Workspace</div>

            <div
                style={homeStyle}
                onClick={() => history.push('/home')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') history.push('/home'); }}
                data-testid="nav-home"
            >
                <Icon name="trending_up" size={16} color={homeIconColor} />
                Home
            </div>

            <div
                style={suggestionsStyle}
                onClick={() => history.push('/suggestions')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') history.push('/suggestions'); }}
                data-testid="nav-suggestions"
            >
                <Icon name="sparkle" size={16} color={suggestionsIconColor} />
                Suggestions
            </div>

            <div
                style={templatesStyle}
                onClick={() => history.push('/templates')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') history.push('/templates'); }}
                data-testid="nav-templates"
            >
                <Icon name="archive" size={16} color={templatesIconColor} />
                Templates
            </div>

            {/* Budgets section */}
            <div style={S.section}>Budgets</div>
            <div style={S.budgetList}>
                {visibleBudgets.map((budget, idx) => {
                    const dotColor = LIST_COLORS[idx % LIST_COLORS.length];
                    const budgetName = String(budget.displayName || '');
                    return (
                        <div
                            key={budget.id}
                            style={S.budgetItem}
                            onClick={() => onSelectBudget(budget)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => { if (e.key === 'Enter') onSelectBudget(budget); }}
                            data-testid={`sidebar-budget-${budget.id}`}
                        >
                            <span style={{ ...S.budgetDot, background: dotColor }} />
                            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {budgetName}
                            </span>
                        </div>
                    );
                })}
            </div>

            {/* User section */}
            <div style={S.userSection}>
                <div style={S.userAvatar}>{avatarInitial}</div>
                <div style={S.userInfo}>
                    <div style={S.userName}>{emailStr}</div>
                    <div style={S.userEmail}>{emailStr}</div>
                </div>
                <button
                    style={S.logoutBtn}
                    onClick={onLogout}
                    title="Sign out"
                    data-testid="logout-btn"
                    aria-label="Sign out"
                >
                    <Icon name="exit" size={16} color={C.SLATE} />
                </button>
            </div>
        </div>
    );
}
